import * as XLSX from 'xlsx';
import { PaymentRequisitionWithRelations } from '../../../redux/vmsApi';

type ColumnType = 'text' | 'money' | 'rate' | 'integer' | 'date' | 'datetime';
type CellValue = string | number | Date | null;

interface ExportColumn {
    header: string;
    width: number;
    type: ColumnType;
    value: (req: PaymentRequisitionWithRelations) => unknown;
}

const NUMBER_FORMATS: Partial<Record<ColumnType, string>> = {
    money: '#,##0.00',
    rate: '0.0000',
    integer: '0',
    date: 'yyyy-mm-dd',
    datetime: 'yyyy-mm-dd hh:mm',
};

const toDate = (value: unknown): Date | null => {
    if (!value) return null;
    const parsed = new Date(value as string);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

const personName = (user?: { name?: string; email?: string } | null) =>
    user?.name || user?.email || '';

const COLUMNS: ExportColumn[] = [
    { header: 'PO Number', width: 16, type: 'text', value: r => r.purchaseOrder?.purchaseOrderNumber || (r as any).purchaseOrderNumber },
    { header: 'Requisition Number', width: 20, type: 'text', value: r => r.purchaseOrder?.reqNumber },
    { header: 'PO Title', width: 36, type: 'text', value: r => r.purchaseOrder?.title || r.title },
    { header: 'Supplier', width: 30, type: 'text', value: r => (r.purchaseOrder?.quotation?.supplier as any)?.name },
    { header: 'Development', width: 22, type: 'text', value: r => r.development },
    { header: 'Project', width: 22, type: 'text', value: r => r.project },
    { header: 'Department', width: 20, type: 'text', value: r => r.department },
    { header: 'Payment Type', width: 16, type: 'text', value: r => r.paymentType },
    { header: 'Payment Method', width: 16, type: 'text', value: r => r.paymentMethod },
    { header: 'Status', width: 16, type: 'text', value: r => r.status },
    { header: 'Priority', width: 10, type: 'integer', value: r => r.priority },
    { header: 'Currency', width: 10, type: 'text', value: r => r.currency },
    { header: 'Amount', width: 16, type: 'money', value: r => r.amount },
    { header: 'Rate', width: 12, type: 'rate', value: r => r.rate },
    {
        header: 'USD Equivalent',
        width: 16,
        type: 'money',
        value: r => {
            const amount = toNumber(r.amount);
            const rate = toNumber(r.rate);
            return amount !== null && rate !== null && rate > 0 ? amount / rate : null;
        },
    },
    { header: 'PO Value', width: 16, type: 'money', value: r => r.purchaseOrder?.quotation?.grandTotal },
    { header: 'Reference Number', width: 22, type: 'text', value: r => r.referenceNumber },
    { header: 'Batch Number', width: 18, type: 'text', value: r => r.batchNumber },
    { header: 'Due Date', width: 14, type: 'date', value: r => r.dueDate },
    { header: 'Scheduled For', width: 14, type: 'date', value: r => r.scheduledFor },
    { header: 'Payment Date', width: 14, type: 'date', value: r => r.paymentDate },
    { header: 'Date Created', width: 18, type: 'datetime', value: r => r.dateCreated },
    { header: 'Prepared By', width: 24, type: 'text', value: r => personName(r.preparedBy) },
    { header: 'Reviewed By', width: 24, type: 'text', value: r => personName(r.reviewedBy) },
    { header: 'Reviewed At', width: 18, type: 'datetime', value: r => r.reviewedAt },
    { header: 'Prioritized By', width: 24, type: 'text', value: r => personName(r.prioritizedBy) },
    { header: 'Prioritized At', width: 18, type: 'datetime', value: r => r.prioritizedAt },
    { header: 'Scheduled By', width: 24, type: 'text', value: r => personName(r.scheduledBy) },
    { header: 'Scheduled At', width: 18, type: 'datetime', value: r => r.scheduledAt },
    { header: 'Payment Confirmed By', width: 24, type: 'text', value: r => personName(r.paymentConfirmedBy) },
    { header: 'Payment Confirmed At', width: 18, type: 'datetime', value: r => r.paymentConfirmedAt },
    { header: 'Request ID', width: 28, type: 'text', value: r => r.id },
];

const toCellValue = (type: ColumnType, raw: unknown): CellValue => {
    if (raw === null || raw === undefined || raw === '') return null;
    switch (type) {
        case 'money':
        case 'rate':
        case 'integer':
            return toNumber(raw);
        case 'date':
        case 'datetime':
            return toDate(raw);
        default:
            return String(raw);
    }
};

const buildDataSheet = (rows: PaymentRequisitionWithRelations[]) => {
    const aoa: CellValue[][] = [
        COLUMNS.map(column => column.header),
        ...rows.map(req => COLUMNS.map(column => toCellValue(column.type, column.value(req)))),
    ];

    const sheet = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

    rows.forEach((_, rowIndex) => {
        COLUMNS.forEach((column, columnIndex) => {
            const format = NUMBER_FORMATS[column.type];
            const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })];
            if (format && cell) cell.z = format;
        });
    });

    sheet['!cols'] = COLUMNS.map(column => ({ wch: column.width }));
    sheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: Math.max(rows.length, 1), c: COLUMNS.length - 1 },
        }),
    };

    return sheet;
};

const EXCLUDED_FILTER_KEYS = new Set(['skip', 'limit', 'reportByEmail', 'reportName']);

const buildSummarySheet = (
    rows: PaymentRequisitionWithRelations[],
    filters: Record<string, unknown>,
    reportLabel: string,
) => {
    const totalsByCurrency = new Map<string, { count: number; total: number }>();
    rows.forEach(req => {
        const currency = req.currency || 'N/A';
        const entry = totalsByCurrency.get(currency) ?? { count: 0, total: 0 };
        entry.count += 1;
        entry.total += toNumber(req.amount) ?? 0;
        totalsByCurrency.set(currency, entry);
    });

    const appliedFilters = Object.entries(filters).filter(
        ([key, value]) => !EXCLUDED_FILTER_KEYS.has(key) && value !== '' && value !== false && value != null,
    );

    const aoa: CellValue[][] = [
        ['Report', reportLabel],
        ['Generated', new Date()],
        ['Records', rows.length],
        [],
        ['Filters applied'],
        ...(appliedFilters.length > 0
            ? appliedFilters.map(([key, value]): CellValue[] => [key, String(value)])
            : [['None']]),
        [],
        ['Totals by currency'],
        ['Currency', 'Count', 'Total Amount'],
        ...Array.from(totalsByCurrency.entries()).map(
            ([currency, { count, total }]): CellValue[] => [currency, count, round2(total)],
        ),
    ];

    const sheet = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
    sheet['B2'].z = NUMBER_FORMATS.datetime;

    const totalsStartRow = aoa.length - totalsByCurrency.size;
    for (let i = 0; i < totalsByCurrency.size; i += 1) {
        const cell = sheet[XLSX.utils.encode_cell({ r: totalsStartRow + i, c: 2 })];
        if (cell) cell.z = NUMBER_FORMATS.money;
    }

    sheet['!cols'] = [{ wch: 24 }, { wch: 36 }, { wch: 18 }];
    return sheet;
};

const pad = (value: number) => String(value).padStart(2, '0');

export const exportPaymentRequisitionsToExcel = (params: {
    rows: PaymentRequisitionWithRelations[];
    filters: Record<string, unknown>;
    reportLabel: string;
    fileNamePrefix: string;
}) => {
    const { rows, filters, reportLabel, fileNamePrefix } = params;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, buildDataSheet(rows), 'Payment Requisitions');
    XLSX.utils.book_append_sheet(workbook, buildSummarySheet(rows, filters, reportLabel), 'Summary');

    const now = new Date();
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    XLSX.writeFile(workbook, `${fileNamePrefix}_${stamp}.xlsx`, { cellDates: true });
};
